/**
 * Pathway activity types (Sprint 4.3.2, PROD-651).
 *
 * Domain shape for the orchestration-side `activity` concept introduced
 * by the Awell orchestration ontology. Distinct from the editor-side
 * `action` (which is the design-time prototype): the activity is a
 * runtime instance created when an action is activated. One action can
 * mint many activities (multiplicity).
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/docs/
 *   getting-started/orchestration-ontology.md
 *
 * Field map -> FHIR R4 Task:
 *   - `activity_id`        -> Task.id
 *   - `status`             -> Task.status (mapped via STATUS_TO_TASK + reverse)
 *   - `source_trigger_ref` -> Task.basedOn[0].reference
 *   - `start_date`         -> Task.executionPeriod.start
 *   - `completion_date`    -> Task.executionPeriod.end
 *   - `indirect_object`    -> Task.owner.reference (assignee)
 *   - `action_def_id`      -> Task.instantiatesCanonical
 *
 * Status enum (closed vocabulary; extends Awell ontology):
 *   - `pending`   - activity created, not yet activated (queued)
 *   - `active`    - activity in flight; assignee can act on it
 *   - `completed` - assignee submitted the activity successfully
 *   - `skipped`   - assignee or system bypassed the activity
 *   - `failed`    - activity errored (validation, integration, timeout)
 *   - `expired`   - activity timed out before completion
 *
 * The 6-value enum is stable and load-bearing for the ActivityInspector
 * status-pill rendering. New status values require a coordinated
 * inspector update + visual distinction (HC6 a11y SR labels) so the
 * enum is treated as a closed registry.
 *
 * HC5: zero em / en dashes anywhere.
 */

export const PATHWAY_ACTIVITY_STATUSES = [
  'pending',
  'active',
  'completed',
  'skipped',
  'failed',
  'expired',
] as const;

export type PathwayActivityStatus = (typeof PATHWAY_ACTIVITY_STATUSES)[number];

export interface PathwayActivity {
  /** Stable runtime identifier for the activity (FHIR Task.id). */
  activity_id: string;
  /** Action the activity instantiates (FHIR Task.instantiatesCanonical). */
  action_def_id: string;
  /** Closed status enum. */
  status: PathwayActivityStatus;
  /**
   * Source-trigger reference. Captures the upstream entity that caused
   * the activity to be minted (e.g. `Encounter/abc`, `track-onboarding`).
   */
  source_trigger_ref: string;
  /** ISO timestamp the activity entered an active state. Nullable. */
  start_date: string | null;
  /** ISO timestamp the activity reached a terminal state. Nullable. */
  completion_date: string | null;
  /**
   * Stakeholder assigned to perform the activity. The Awell ontology
   * calls this the `indirect_object` (the activity is performed BY a
   * subject ON an indirect object). For the prototype we store the
   * canonical reference string; the inspector renders the display name.
   */
  indirect_object: string;
  /** Optional human-readable name of the activity (Awell label). */
  label?: string;
}

/**
 * Map a FHIR R4 Task.status onto the closed PathwayActivityStatus enum.
 * Aidbox / FHIR Task supports a wider vocabulary; the table below
 * collapses the long tail onto the 6-value runtime enum so the
 * inspector renders consistently regardless of the upstream encoding.
 *
 * Reference: hl7.org/fhir/R4/valueset-task-status.html
 */
const TASK_STATUS_TO_ACTIVITY: Record<string, PathwayActivityStatus> = {
  draft: 'pending',
  requested: 'pending',
  received: 'pending',
  ready: 'pending',
  accepted: 'active',
  'in-progress': 'active',
  'on-hold': 'active',
  completed: 'completed',
  rejected: 'skipped',
  cancelled: 'skipped',
  failed: 'failed',
  'entered-in-error': 'failed',
};

/** Canonical mapper. Falls back to `pending` when the status is unknown. */
export function mapTaskStatusToActivityStatus(
  status: string | undefined | null,
): PathwayActivityStatus {
  if (!status) return 'pending';
  return TASK_STATUS_TO_ACTIVITY[status] ?? 'pending';
}
